import React, { useMemo, useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from "react";
import PropTypes from "prop-types";
import { Checkbox, Spinner } from "@blueprintjs/core";
import axios from "axios";
import Cookies from "js-cookie";
import debounce from "lodash/debounce";
import "./manage_access.css";

function formatUser(user, index) {
  const userId = String(user?.id || user?.user_id || `user_${index}`);
  const userName = user?.full_name || user?.name || user?.email || "";
  const email = user?.email || "";
  const avatarUrl = user?.image_url || user?.image_file_name || user?.avatar || null;
  const color = user?.color;
  const initials =
    user?.initials ||
    (userName
      ? userName
          .split(" ")
          .map(word => word[0])
          .slice(0, 2)
          .join("")
          .toUpperCase()
      : "U");

  return {
    id: userId,
    name: userName,
    email,
    avatarUrl,
    color,
    initials
  };
}

const UserListSelector = forwardRef(function UserListSelector(
  { users = [], selectedIds = [], onChange, query: externalQuery, onQueryChange, apiBase, isOpen = false, pageSize = 100 },
  ref
) {
  const [internalQuery, setInternalQuery] = useState("");
  const query = typeof externalQuery === "string" ? externalQuery : internalQuery;

  useEffect(() => {
    if (typeof externalQuery === "string" && typeof onQueryChange === "function") {
      onQueryChange(query);
    }
  }, [query, externalQuery, onQueryChange]);

  const [selectedUserIds, setSelectedUserIds] = useState(() => {
    return Array.isArray(selectedIds) ? selectedIds.map(String) : [];
  });

  useEffect(() => {
    setSelectedUserIds(Array.isArray(selectedIds) ? selectedIds.map(String) : []);
  }, [selectedIds]);

  const [fetchedUsers, setFetchedUsers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMorePages, setHasMorePages] = useState(true);
  const [isLoadingPage, setIsLoadingPage] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  const listRef = useRef(null);
  const serverSelectionRef = useRef(null);
  const abortControllerRef = useRef(null);
  const isLoadingRef = useRef(false);
  const isOpenRef = useRef(isOpen);

  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  const apiUrl = apiBase || Cookies.get("apiEnv");
  const hasQuery = (query || "").trim().length > 0;

  const fetchUsers = useCallback(
    async (pageNumber = 1, searchQuery = "") => {
      if (!isOpenRef.current) return { users: [], hasMore: false };
      if (!apiUrl) return { users: [], hasMore: false };
      if (isLoadingRef.current) return { users: [], hasMore: true };

      try {
        abortControllerRef.current?.abort();
      } catch (error) {
        // Ignore abort errors
      }
      abortControllerRef.current = new AbortController();

      isLoadingRef.current = true;
      const trimmedQuery = (searchQuery || "").trim();

      try {
        if (pageNumber === 1) {
          setIsSearching(!!trimmedQuery);
          setIsInitialLoading(true);
        } else {
          setIsLoadingPage(true);
        }

        const url = `${apiUrl}/roles/user_list_for_role?page=${pageNumber}&per_page=${pageSize}${
          trimmedQuery ? `&search=${encodeURIComponent(trimmedQuery)}` : ""
        }`;

        const response = await axios.get(url, { signal: abortControllerRef.current.signal });
        const usersList = Array.isArray(response?.data) ? response.data : response?.data?.users || response?.data?.data || [];
        const formattedUsers = (Array.isArray(usersList) ? usersList : []).map((user, index) =>
          formatUser(user, (pageNumber - 1) * pageSize + index)
        );
        const hasMoreData = formattedUsers.length >= pageSize;
        return { users: formattedUsers, hasMore: hasMoreData };
      } catch (error) {
        if (axios.isCancel?.(error) || error?.name === "CanceledError" || error?.name === "AbortError") {
          return { users: [], hasMore: true };
        }
        return { users: [], hasMore: false };
      } finally {
        isLoadingRef.current = false;
        setIsInitialLoading(false);
        setIsLoadingPage(false);
        setIsSearching(false);
      }
    },
    [apiUrl, pageSize]
  );

  const debouncedSearch = useRef(
    debounce(async searchQuery => {
      if (!isOpenRef.current) return;
      const { users, hasMore } = await fetchUsers(1, searchQuery);
      setFetchedUsers(users);
      setHasMorePages(hasMore);
      setCurrentPage(1);
    }, 500)
  ).current;

  const prevStateRef = useRef({ isOpen: false, query: undefined });
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      hasFetchedRef.current = false;
      prevStateRef.current = { isOpen, query };
      return;
    }

    const justOpened = !prevStateRef.current.isOpen && isOpen;
    const hasQueryChanged = prevStateRef.current.query !== query;

    if (justOpened) {
      if (!hasFetchedRef.current) {
        (async () => {
          setCurrentPage(1);
          setHasMorePages(true);
          const { users, hasMore } = await fetchUsers(1, query);
          setFetchedUsers(users);
          setHasMorePages(hasMore);
          setCurrentPage(1);
        })();
        hasFetchedRef.current = true;
      }
    } else if (hasQueryChanged) {
      setHasMorePages(true);
      setCurrentPage(1);
      setFetchedUsers([]);
      debouncedSearch(query);
    }

    prevStateRef.current = { isOpen, query };
  }, [isOpen, query, fetchUsers, debouncedSearch]);

  useEffect(() => {
    let isMounted = true;
    if (!isOpen) return;
    if (currentPage <= 1) return;

    (async () => {
      const { users, hasMore } = await fetchUsers(currentPage, query);
      if (!isMounted) return;
      setFetchedUsers(prev => [...prev, ...users]);
      setHasMorePages(hasMore);
    })();

    return () => {
      isMounted = false;
    };
  }, [currentPage, isOpen, query, fetchUsers]);

  const getAllUsers = useMemo(() => {
    const assignedUsers = Array.isArray(users) ? users.map((user, index) => formatUser(user, index)) : [];
    const allUsersList = [...assignedUsers, ...fetchedUsers];
    const usersMap = new Map();
    allUsersList.forEach(user => {
      if (!user || !user.id) return;
      if (!usersMap.has(String(user.id))) {
        usersMap.set(String(user.id), user);
      }
    });
    return Array.from(usersMap.values());
  }, [users, fetchedUsers]);

  const displayUsers = useMemo(() => {
    if (hasQuery) return fetchedUsers;

    const assignedUsers = Array.isArray(users) ? users.map((user, index) => formatUser(user, index)) : [];
    const allUsersList = [...assignedUsers, ...fetchedUsers];
    const usersMap = new Map();
    allUsersList.forEach(user => {
      if (!user || !user.id) return;
      if (!usersMap.has(String(user.id))) {
        usersMap.set(String(user.id), user);
      }
    });
    return Array.from(usersMap.values());
  }, [hasQuery, users, fetchedUsers]);

  useImperativeHandle(
    ref,
    () => ({
      getSelected: () => {
        if (serverSelectionRef.current && typeof serverSelectionRef.current === "object") {
          return serverSelectionRef.current;
        }
        return Array.from(selectedUserIds);
      },
      setSelected: (userIds = []) => {
        serverSelectionRef.current = null;
        setSelectedUserIds(Array.isArray(userIds) ? userIds.map(String) : []);
      },
      refresh: async () => {
        if (!isOpenRef.current) return;
        serverSelectionRef.current = null;
        const { users, hasMore } = await fetchUsers(1, query);
        setFetchedUsers(users);
        setHasMorePages(hasMore);
        setCurrentPage(1);
      }
    }),
    [selectedUserIds, fetchUsers, query]
  );

  const toggleUser = userId => {
    serverSelectionRef.current = null;
    const updatedSelection = selectedUserIds.includes(userId) ? selectedUserIds.filter(id => id !== userId) : [...selectedUserIds, userId];
    setSelectedUserIds(updatedSelection);
    onChange?.(updatedSelection);
  };

  const toggleSelectAll = () => {
    const displayUserIds = displayUsers.map(user => user.id);
    if (displayUserIds.length === 0) return;

    const areAllSelected = displayUserIds.length > 0 && displayUserIds.every(id => selectedUserIds.includes(id));
    const isFirstPage = currentPage === 1;
    const isFirstPageFull = fetchedUsers.length >= pageSize || getAllUsers.length >= pageSize;
    const shouldUseServerSelectAll = isFirstPage && isFirstPageFull && displayUserIds.length > 0;

    if (shouldUseServerSelectAll) {
      if (areAllSelected) {
        setSelectedUserIds([]);
        serverSelectionRef.current = { remove_all: true, search: (query || "").trim() || undefined };
      } else {
        const updatedSelection = Array.from(new Set([...selectedUserIds, ...displayUserIds]));
        setSelectedUserIds(updatedSelection);
        serverSelectionRef.current = { select_all: true, search: (query || "").trim() || undefined };
      }
      return;
    }

    serverSelectionRef.current = null;
    const updatedSelection = areAllSelected
      ? selectedUserIds.filter(id => !displayUserIds.includes(id))
      : Array.from(new Set([...selectedUserIds, ...displayUserIds]));
    setSelectedUserIds(updatedSelection);
    onChange?.(updatedSelection);
  };

  useEffect(() => {
    const listElement = listRef.current;
    if (!listElement) return;

    let isTicking = false;
    const handleScroll = () => {
      if (isTicking) return;
      isTicking = true;
      requestAnimationFrame(() => {
        isTicking = false;
        if (!isOpenRef.current) return;
        const { scrollTop, scrollHeight, clientHeight } = listElement;
        const scrollThreshold = 300;
        if (scrollHeight - (scrollTop + clientHeight) <= scrollThreshold) {
          if (hasMorePages && !isLoadingRef.current) {
            setCurrentPage(prev => Math.max(2, prev + 1));
          }
        }
      });
    };

    listElement.addEventListener("scroll", handleScroll);
    return () => listElement.removeEventListener("scroll", handleScroll);
  }, [hasMorePages]);

  return (
    <div className="uls-root">
      <div className="uls-list" ref={listRef} style={{ overflowY: "auto" }}>
        <div className="uls-list-header uls-grid">
          <div className="uls-col-check">
            <Checkbox
              className="ma-checkbox"
              checked={displayUsers.length > 0 && displayUsers.every(user => selectedUserIds.includes(user.id))}
              onChange={toggleSelectAll}
            />
          </div>
          <div className="uls-col-main">Users name</div>
          <div className="uls-col-email">Email</div>
        </div>

        {isInitialLoading || isSearching ? (
          <div className="ma-spinner" style={{ padding: 20, textAlign: "center" }}>
            <Spinner size={24} />
            <div style={{ marginTop: 8 }}>{isSearching ? "Searching…" : "Loading users…"}</div>
          </div>
        ) : displayUsers.length === 0 ? (
          <div className="uls-empty">No users found</div>
        ) : (
          <>
            {displayUsers?.map(user => {
              const isChecked = selectedUserIds.includes(user.id);
              return (
                <label key={user.id} className="uls-row uls-grid" onMouseDown={e => e.preventDefault()} style={{ background: "#fff" }}>
                  <div className="uls-col-check">
                    <Checkbox className="ma-checkbox" checked={isChecked} onChange={() => toggleUser(user.id)} />
                  </div>

                  <div className="uls-col-main-row">
                    {user.avatarUrl ? (
                      <img src={user.avatarUrl} alt={user.name} className="uls-avatar-img" />
                    ) : (
                      <div className="uls-avatar" style={{ background: user.color || "#9ca3af", marginBottom: 0 }}>
                        {user.initials}
                      </div>
                    )}
                    <div className="uls-meta">
                      <div className="uls-name">{user.name || user.email}</div>
                    </div>
                  </div>

                  <div className="uls-col-email">{user.email}</div>
                </label>
              );
            })}
            {isLoadingPage && (
              <div style={{ padding: 12, display: "flex", justifyContent: "center" }}>
                <Spinner size={18} />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
});

UserListSelector.propTypes = {
  users: PropTypes.array,
  selectedIds: PropTypes.array,
  onChange: PropTypes.func,
  query: PropTypes.string,
  onQueryChange: PropTypes.func,
  apiBase: PropTypes.string,
  isOpen: PropTypes.bool,
  pageSize: PropTypes.number
};

export default UserListSelector;
